/**
 * historyApi.ts — thin REST client for the server-side Copilot conversation
 * history (gen-ai, RP-2585). Server-owned, per-user, token-scoped: identity is
 * derived server-side from the SAME end-user token the chat WS uses, so we send
 * NO user_id and cross-user access is a 404.
 *
 * Base URL: config.copilot_api_url (…/api/v1/copilot); this client appends
 * /conversations. Auth: `x-access-token: <token>` from getJsonCookies().
 *
 * The active session is rendered from live WS frames — this REST client is only
 * used to LIST past conversations, LOAD a selected transcript, and DELETE one.
 * See docs/copilot/plans/copilot-history-client-gosite-react.md.
 */
import config from '@config/index'
import { getJsonCookies } from '@helpers/utility'
import {
  ConversationListResponse,
  ConversationDetailResponse,
  CopilotMessage,
  HistoryErrorKind,
  TranscriptMessage
} from './copilotTypes'
import { widgetsFrom } from '../widgets/extract'

/** typed history-API failure — callers branch on `.kind`, not HTTP status. */
export class CopilotHistoryError extends Error {
  kind: HistoryErrorKind
  status?: number
  constructor(kind: HistoryErrorKind, message: string, status?: number) {
    super(message)
    this.name = 'CopilotHistoryError'
    this.kind = kind
    this.status = status
  }
}

const baseUrl = (): string => (config as { copilot_api_url?: string }).copilot_api_url || ''

const authToken = (): string => {
  const token = getJsonCookies()
  if (!token) throw new CopilotHistoryError('auth', 'Missing copilot access token')
  return token
}

const statusToKind = (status: number): HistoryErrorKind => {
  if (status === 401 || status === 403) return 'auth'
  if (status === 404) return 'not-found'
  if (status === 502 || status === 503 || status >= 500) return 'transient'
  return 'unknown'
}

/** shared fetch wrapper: adds auth header, maps non-2xx + network faults to typed errors. */
const request = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const base = baseUrl()
  if (!base) throw new CopilotHistoryError('unknown', 'Missing config.copilot_api_url')
  const token = authToken()

  let res: Response
  try {
    res = await fetch(`${base}${path}`, {
      ...init,
      headers: {
        'x-access-token': token,
        Accept: 'application/json',
        ...(init && init.headers)
      }
    })
  } catch (err) {
    // network / CORS / DNS — retryable from the user's perspective
    throw new CopilotHistoryError('transient', 'Network error reaching copilot history', undefined)
  }

  if (!res.ok) {
    throw new CopilotHistoryError(
      statusToKind(res.status),
      `Copilot history request failed (${res.status})`,
      res.status
    )
  }

  // DELETE may return an empty body; guard the JSON parse.
  if (res.status === 204) return undefined as unknown as T
  try {
    return (await res.json()) as T
  } catch (err) {
    return undefined as unknown as T
  }
}

/** list the caller's sessions, newest first (server-sorted); soft-deleted excluded. */
export const listConversations = (opts: { page?: number; limit?: number } = {}): Promise<ConversationListResponse> => {
  const page = opts.page || 1
  const limit = opts.limit || 25
  return request<ConversationListResponse>(`/conversations?page=${page}&limit=${limit}`)
}

/** full transcript for one session, ordered by seq (oldest→newest), ownership-checked. */
export const getConversation = (
  sessionId: string,
  opts: { page?: number; limit?: number } = {}
): Promise<ConversationDetailResponse> => {
  const page = opts.page || 1
  const limit = opts.limit || 200
  return request<ConversationDetailResponse>(
    `/conversations/${encodeURIComponent(sessionId)}?page=${page}&limit=${limit}`
  )
}

/** load an entire transcript across pages (v1: sequential, capped). */
export const getFullTranscript = async (
  sessionId: string,
  limit = 200,
  maxPages = 10
): Promise<TranscriptMessage[]> => {
  const first = await getConversation(sessionId, { page: 1, limit })
  const messages = [...first.messages]
  const pages = Math.min(first.pagination ? first.pagination.pages : 1, maxPages)
  for (let page = 2; page <= pages; page += 1) {
    const next = await getConversation(sessionId, { page, limit })
    messages.push(...next.messages)
  }
  return messages
}

/** soft-delete a session (excluded from the list; transcript retained server-side). */
export const deleteConversation = async (sessionId: string): Promise<void> => {
  await request<void>(`/conversations/${encodeURIComponent(sessionId)}`, { method: 'DELETE' })
}

/**
 * Map a persisted transcript to the render-layer CopilotMessage[]. Skips assistant
 * `preamble` entries so a resumed session matches the live WS chat UX (the WS path
 * never renders standalone preambles), and carries voice/intent through. The chip
 * mirrors the live deriveChip: an 'unknown' intent renders no chip (RP-2694).
 */
export const transcriptToMessages = (messages: TranscriptMessage[]): CopilotMessage[] =>
  messages
    .filter((m) => !(m.role === 'assistant' && m.metadata && m.metadata.kind === 'preamble'))
    .map((m) => ({
      id: m.turn_id ? `${m.turn_id}_${m.seq}` : `seq_${m.seq}`,
      role: m.role,
      text: m.text,
      voice: m.metadata ? m.metadata.voice : undefined,
      chip:
        m.role === 'assistant' && m.metadata && m.metadata.intent && m.metadata.intent !== 'unknown'
          ? m.metadata.intent
          : undefined,
      // replay the persisted display widgets + their hierarchy verbatim (RP-2714)
      widgets: m.role === 'assistant' ? widgetsFrom(m.metadata) : undefined
    }))
